import React from 'react';
import { motion } from 'framer-motion';
import { useExperience } from '../../contexts/ExperienceContext';
import { needById, needs } from '../../data/needs';
import { brand } from '../../data/palette';
import { productById } from '../../data/products';
import { Advance } from '../ui/Advance';
import { ProductGlyph } from '../ui/ProductGlyph';
import { Eyebrow, Stage, StageTitle } from '../ui/Stage';
const ease = [0.16, 1, 0.3, 1] as const;

/** The whole family, finally — three members and one cousin. */
export function FamilyStage() {
  const {
    next,
    direction,
    primaryNeed
  } = useExperience();
  const memberNeeds = needs.filter(need => need.id !== 'handling');
  const cousinNeed = needById('handling');
  const cousin = productById(cousinNeed.productId);
  return <Stage direction={direction} align="start" className="min-h-screen py-28 sm:py-32" labelledBy="family-title" data-id="element-237" data-mp="1i8q9509vdgau">
      <div className="flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between" data-id="element-238" data-mp="pkuh48v8wcke">
        <div data-id="element-239" data-mp="32a9tqktqqv1p">
          <Eyebrow accent={brand.green} data-id="element-240" data-mp="328gh8owg5gls">The Filtek family</Eyebrow>
          <StageTitle id="family-title" size="display-sm" className="mt-6 max-w-xl" data-id="element-241" data-mp="378bc13jceqc4">
            Three members. One cousin.
          </StageTitle>
        </div>
        <p className="label-mono text-[10px] text-mute" data-id="element-242" data-mp="3c031faqriq0o">Need → Technique → Solution</p>
      </div>

      <div className="mt-14 grid gap-x-8 gap-y-12 md:grid-cols-3" data-id="element-243" data-mp="3gxl1ebmsxuwj">
        {memberNeeds.map((need, index) => {
        const product = productById(need.productId);
        const isFocus = need.id === primaryNeed;
        return <motion.div key={need.id} initial={{
          opacity: 0,
          y: 26
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.75,
          delay: index * 0.12,
          ease
        }} className="relative flex flex-col pt-5" data-id="element-244" data-mp="2t91tvybktm5i" data-mpi={index}>
              <span className="absolute inset-x-0 top-0 h-px" style={{
            backgroundColor: need.accent,
            opacity: isFocus ? 1 : 0.45
          }} aria-hidden="true" data-id="element-245" data-mp="ck65omfp9t9e" />
              {isFocus && <span className="pointer-events-none absolute -inset-x-4 -bottom-5 top-0 -z-10" style={{
            backgroundColor: need.accent,
            opacity: 0.07
          }} aria-hidden="true" data-id="element-246" data-mp="24ko2jter8aij" />}

              <div className="flex items-center justify-between" data-id="element-247" data-mp="1pwt8a8l3ly3c">
                <span className="label-mono text-[9px]" style={{
              color: need.accent
            }} data-id="element-248" data-mp="1px2wprqhvx6x">
                  When you need
                </span>
                {isFocus && <span className="label-mono text-[8px] text-enamel" data-id="element-249" data-mp="1zmw7wrmq6aan">Your focus</span>}
              </div>

              <p className="mt-5 text-xl font-normal leading-tight tracking-[-0.01em] text-enamel" data-id="element-250" data-mp="1uuv0bj0i57qy">
                {need.label}
              </p>

              <p className="mt-4 border-t border-enamel/10 pt-4 text-sm font-light leading-relaxed text-mute" data-id="element-251" data-mp="1748ulydyzzk4">
                {need.technique}
              </p>

              <div className="mt-6 flex items-end gap-4 border-t border-enamel/10 pt-6" data-id="element-252" data-mp="3dtsld7gtaj1i">
                <ProductGlyph form={product.form} accent={need.accent} className="h-20 w-10 shrink-0" data-id="element-253" data-mp="3imc92x97quk1" />
                <div data-id="element-254" data-mp="39sqt5albb791">
                  <p className="text-lg font-normal leading-tight" style={{
                color: need.accent
              }} data-id="element-255" data-mp="39u1zni656t3u">
                    {product.name}
                  </p>
                  <p className="mt-2 text-xs font-light leading-snug text-mute" data-id="element-256" data-mp="5dh8lvztitcj">
                    {product.descriptor}
                  </p>
                </div>
              </div>
            </motion.div>;
      })}
      </div>

      {/* The cousin — related, but not a composite */}
      <motion.div initial={{
      opacity: 0,
      y: 24
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.8,
      delay: 0.5,
      ease
    }} className="relative mt-16 border-t border-dashed pt-8" style={{
      borderColor: `${cousinNeed.accent}55`
    }} data-id="element-257" data-mp="2c4gu94gtozm5">
        <div className="flex flex-col gap-8 sm:flex-row sm:items-center sm:gap-10" data-id="element-258" data-mp="2rm6z17hmj8e5">
          <ProductGlyph form={cousin.form} accent={cousinNeed.accent} className="h-24 w-16 shrink-0" data-id="element-259" data-mp="2nl4xgk08ik90" />
          <div className="flex-1" data-id="element-260" data-mp="2ioegzvkipmn2">
            <span className="label-mono text-[9px]" style={{
            color: cousinNeed.accent
          }} data-id="element-261" data-mp="3fckfe0qujisw">
              And the cousin
            </span>
            <p className="mt-4 text-xl font-normal leading-tight text-enamel sm:text-2xl" data-id="element-262" data-mp="3bck42oz1nnq2">
              {cousin.name}
            </p>
            <p className="mt-3 max-w-2xl text-base font-light leading-relaxed text-mute" data-id="element-263" data-mp="36e8qc35mot2k">
              {cousin.kinship} Warmed composite flows and adapts more easily — so the same three
              members handle better in the cases that fight you.
            </p>
          </div>
        </div>
      </motion.div>

      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      duration: 0.9,
      delay: 0.9
    }} className="mt-20 flex flex-col gap-10 border-t border-enamel/10 pt-12 lg:flex-row lg:items-end lg:justify-between" data-id="element-264" data-mp="1ftzbb4l0s4dj">
        <p className="max-w-xl font-heading text-[1.4rem] leading-snug tracking-[-0.01em] text-enamel sm:text-[1.8rem]" data-id="element-265" data-mp="1pqu5jba9fim0">
          Different cases. Different demands.
          <span className="block text-mute" data-id="element-266" data-mp="1yjfbtrwpfwty">Fewer compromises.</span>
        </p>
        <Advance label="Go deeper" onClick={next} accent={brand.green} data-id="element-267" data-mp="23j0loxdexplg" />
      </motion.div>
    </Stage>;
}